import React, { MutableRefObject } from 'react';
import { withLocalePrefix } from 'shared/utils/translation';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import styled from 'styled-components';
import SearchIcon from '@grnhse/seedling/lib/birch/icons/search';
import { Chip } from '@grnhse/seedling/lib/birch/components/chips';
import makeStyles from '@mui/styles/makeStyles';
import TypographyTheme from '@grnhse/seedling/lib/birch/typography/theme';
import Theme from '@grnhse/seedling/lib/birch//components/forms/text_input/theme';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import textInputTheme from '@grnhse/seedling/lib/birch/components/forms/text_input/theme';

const t = withLocalePrefix('talent_search.form.sections.keywords');

type Props = {
  onAdd: (keywords: string[]) => void;
  onRemove: (keyword: string) => void;
  values: string[];
  booleanIndicator?: string;
  inputRef: MutableRefObject<HTMLElement | null>;
};

const StartAdornmentWrapper = styled.div`
  display: flex;
`;

const useStyles = makeStyles({
  inputRoot: {
    '&&[class*="MuiOutlinedInput-root"]': {
      paddingLeft: '16px',
      paddingTop: '4px',
      paddingBottom: '4px',
      outline: 'none',
      '&.Mui-focused fieldset': {
        boxShadow: `inset 0px -2px 0px ${Theme.focused.borderBottomColor}`,
      },
      '& span': {
        margin: '4px 0px 4px 4px',
      },
      '& fieldset': {
        borderRadius: '4px 4px 0 0',
        borderColor: `${textInputTheme.default.borderColor}`,
      },
      '& input': {
        border: 'none',
      },
    },
  },
  inputWrapper: {
    '&&&&': {
      borderRadius: '4px',
      fontFamily: TypographyTheme.fonts.sans,
      fontSize: '1rem',
      lineHeight: '1.5rem',
      color: Theme.default.color,
      WebkitTextFillColor: Theme.default.color,
      '& input': {
        border: 'none',
      },
      '&&.MuiInputBase-multiline': {
        padding: 0,
      },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderTextField = (props: any, placeholder: string) => {
  return (
    <TextField
      {...props}
      placeholder={placeholder}
      fullWidth
      InputProps={{
        ...props.InputProps,
        startAdornment: (
          <>
            <StartAdornmentWrapper>
              <SearchIcon />
            </StartAdornmentWrapper>
            {props.InputProps.startAdornment}
          </>
        ),
      }}
    />
  );
};

const KeywordInput = ({
  onAdd,
  onRemove,
  values,
  booleanIndicator,
  inputRef,
}: Props) => {
  const classes = useStyles();

  const onAddItem = (keywords: string[]) => {
    const recentKeyword = keywords.at(-1) as string;

    // if adding new keyword, check if it is already in the list regardless of casing
    // skip if doing deletion (pressing backspace)
    if (
      keywords.length > values.length &&
      values
        .map((value) => value.toLowerCase())
        .includes(recentKeyword?.toLowerCase())
    ) {
      return;
    }

    onAdd(keywords);
  };

  return (
    <Autocomplete
      clearIcon={false}
      options={[]}
      freeSolo
      multiple
      fullWidth
      onChange={(e, keywords) => {
        e.preventDefault();
        // send array of keywords
        onAddItem(keywords);
      }}
      value={values}
      classes={{
        inputRoot: classes.inputRoot,
        input: classes.inputWrapper,
      }}
      renderTags={(tags) => {
        return tags.map((option, index) => (
          <>
            <Chip text={option} key={index} onRemove={() => onRemove(option)} />
            {booleanIndicator && index !== tags.length - 1 && (
              <StyledInidicator metadata>{booleanIndicator}</StyledInidicator>
            )}
          </>
        ));
      }}
      renderInput={(params) => {
        return renderTextField(
          { ...params, inputRef },
          values.length ? '' : t('placeholder')
        );
      }}
    />
  );
};

const StyledInidicator = styled(SecondaryBody)`
  padding: 8px;
`;

export default KeywordInput;
